/**
 * Deal commissions (BOM Section D): representation, finder's fees and
 * third-party commissions, with 10% EWT withheld from the payee.
 *
 * On the lead they can be added, edited and deleted by someone with
 * leads.manage_commission while the lead is open; once it is confirmed they are
 * locked. On the project the same list is read-only and shows the payable each
 * commission became when the project was completed.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { peso } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

export interface CommissionRow {
  id: string;
  category: string;
  categoryLabel: string;
  payee: string;
  grossAmount: string;
  ewtRate: string;
  ewtAmount: string;
  netPayable: string;
  notes: string | null;
  source?: string;
  payable: { id: string; code: string; status: string; amount: string } | null;
}

interface CommissionList {
  lead: { id: string; code: string; locked: boolean };
  commissions: CommissionRow[];
  totals: { count: number; grossAmount: string; ewtAmount: string; netPayable: string };
}

const CATEGORIES = [
  { code: 'REPRESENTATION', label: 'Representation' },
  { code: 'FINDERS_FEE', label: "Finder's fee" },
  { code: 'THIRD_PARTY_COMMISSION', label: 'Third-party commission' },
];

const PAYABLE_TONE: Record<string, 'ok' | 'warning' | 'critical' | 'neutral'> = {
  PENDING: 'warning',
  COMPLETED: 'ok',
  DISAPPROVED: 'critical',
  CANCELLED: 'neutral',
};

const pct = (rate: string): string => `${Math.round(Number(rate) * 10000) / 100}%`;

interface Draft {
  id?: string;
  category: string;
  payee: string;
  grossAmount: string;
  ewtRate: string;
  notes: string;
}

const EMPTY: Draft = { category: 'REPRESENTATION', payee: '', grossAmount: '', ewtRate: '10', notes: '' };

/** The table itself, shared by the lead (editable) and the project (read-only). */
function CommissionTable({
  rows,
  totals,
  onEdit,
  onDelete,
}: {
  rows: CommissionRow[];
  totals: { grossAmount: string; ewtAmount: string; netPayable: string };
  onEdit?: (row: CommissionRow) => void;
  onDelete?: (row: CommissionRow) => void;
}): JSX.Element {
  const editable = Boolean(onEdit || onDelete);
  return (
    <TableWrap>
      <thead>
        <tr>
          <Th>Category</Th>
          <Th>Payee</Th>
          <Th align="right">Gross</Th>
          <Th align="right">EWT withheld</Th>
          <Th align="right">Net payable</Th>
          <Th>Payable</Th>
          {editable && <Th align="right">Actions</Th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
        {rows.map((row) => (
          <tr key={row.id}>
            <Td>{row.categoryLabel}</Td>
            <Td>
              <p className="font-medium">{row.payee}</p>
              {row.notes && <p className="text-xs text-slate-500">{row.notes}</p>}
              {row.source === 'API' && <p className="text-xs text-slate-400">pushed through the API</p>}
            </Td>
            <Td align="right" className="tabular">
              {peso(row.grossAmount)}
            </Td>
            <Td align="right" className="tabular">
              {peso(row.ewtAmount)}
              <p className="text-xs text-slate-500">{pct(row.ewtRate)}</p>
            </Td>
            <Td align="right" className="tabular font-semibold">
              {peso(row.netPayable)}
            </Td>
            <Td>
              {row.payable ? (
                <StatusChip tone={PAYABLE_TONE[row.payable.status] ?? 'neutral'}>
                  {row.payable.code} · {row.payable.status.toLowerCase()}
                </StatusChip>
              ) : (
                <span className="text-xs text-slate-500">on completion</span>
              )}
            </Td>
            {editable && (
              <Td align="right">
                <div className="flex justify-end gap-1">
                  {onEdit && (
                    <Button variant="ghost" size="sm" aria-label={`Edit ${row.payee}`} onClick={() => onEdit(row)}>
                      <Pencil aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button variant="ghost" size="sm" aria-label={`Delete ${row.payee}`} onClick={() => onDelete(row)}>
                      <Trash2 aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </Td>
            )}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-slate-200 font-semibold dark:border-slate-700">
          <Td>Total</Td>
          <Td>{null}</Td>
          <Td align="right" className="tabular">
            {peso(totals.grossAmount)}
          </Td>
          <Td align="right" className="tabular">
            {peso(totals.ewtAmount)}
          </Td>
          <Td align="right" className="tabular">
            {peso(totals.netPayable)}
          </Td>
          <Td>{null}</Td>
          {editable && <Td>{null}</Td>}
        </tr>
      </tfoot>
    </TableWrap>
  );
}

/** On the lead: list, totals, and add / edit / delete while the lead is open. */
export function LeadCommissionsCard({
  leadId,
  locked,
  isGovernment,
}: {
  leadId: string;
  locked: boolean;
  isGovernment: boolean;
}): JSX.Element | null {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleting, setDeleting] = useState<CommissionRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const visible = can('leads.view_commission');
  const manage = can('leads.manage_commission') && !locked;

  const list = useQuery({
    queryKey: ['lead-commissions', leadId],
    queryFn: () => api<CommissionList>(`/leads/${leadId}/commissions`),
    enabled: visible,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['lead-commissions', leadId] });
    // Net profit and margin on the lead move with the commissions.
    void queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
  };

  const save = useMutation({
    mutationFn: (value: Draft) => {
      const body = {
        category: value.category,
        payee: value.payee.trim(),
        grossAmount: Number(value.grossAmount),
        ewtRate: Number(value.ewtRate) / 100,
        notes: value.notes.trim() || null,
      };
      return value.id
        ? api(`/lead-commissions/${value.id}`, { method: 'PATCH', body })
        : api(`/leads/${leadId}/commissions`, { method: 'POST', body });
    },
    onSuccess: () => {
      setDraft(null);
      setError(null);
      refresh();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not save the commission.'),
  });

  const remove = useMutation({
    mutationFn: (row: CommissionRow) => api(`/lead-commissions/${row.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleting(null);
      refresh();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not delete the commission.'),
  });

  if (!visible) return null;
  const rows = list.data?.commissions ?? [];

  return (
    <Card
      title="Commissions"
      description="Representation, finder's fees and third-party commissions (BOM Section D). Internal only; they come off gross profit, and 10% EWT is withheld from the payee. Each becomes a payable when the project is completed."
      actions={
        manage ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setError(null);
              setDraft({ ...EMPTY });
            }}
          >
            <Plus aria-hidden="true" className="h-4 w-4" /> Add commission
          </Button>
        ) : locked && rows.length > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Lock aria-hidden="true" className="h-3.5 w-3.5" /> Locked with the lead
          </span>
        ) : undefined
      }
    >
      {isGovernment && rows.length > 0 && (
        <div className="mb-3">
          <Alert tone="warning">
            Government client: a payout linked to an official carries RA 3019 / RA 6713 exposure.
          </Alert>
        </div>
      )}
      {error && !draft && (
        <div className="mb-3">
          <Alert tone="critical">{error}</Alert>
        </div>
      )}
      {list.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState title="No commissions on this deal" description={manage ? 'Add one, or let Anna push the BOM Section D.' : undefined} />
      ) : (
        <CommissionTable
          rows={rows}
          totals={list.data!.totals}
          onEdit={
            manage
              ? (row) => {
                  setError(null);
                  setDraft({
                    id: row.id,
                    category: row.category,
                    payee: row.payee,
                    grossAmount: row.grossAmount,
                    ewtRate: String(Number(row.ewtRate) * 100),
                    notes: row.notes ?? '',
                  });
                }
              : undefined
          }
          onDelete={manage ? (row) => setDeleting(row) : undefined}
        />
      )}

      {draft && (
        <Modal
          title={draft.id ? 'Edit commission' : 'Add commission'}
          description="Gross amount in pesos (no VAT). The EWT is withheld from the payee."
          onClose={() => {
            setDraft(null);
            setError(null);
          }}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate(draft);
            }}
          >
            <Field label="Category" required>
              <select
                className={inputClass}
                value={draft.category}
                onChange={(event) => setDraft({ ...draft, category: event.target.value })}
              >
                {CATEGORIES.map((category) => (
                  <option key={category.code} value={category.code}>
                    {category.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Payee" required hint="A person or company; a registered supplier of the same name is linked">
              <input
                className={inputClass}
                value={draft.payee}
                minLength={2}
                maxLength={200}
                required
                onChange={(event) => setDraft({ ...draft, payee: event.target.value })}
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Gross amount" required>
                <input
                  className={inputClass}
                  type="number"
                  inputMode="decimal"
                  min="0.01"
                  step="0.01"
                  required
                  value={draft.grossAmount}
                  onChange={(event) => setDraft({ ...draft, grossAmount: event.target.value })}
                />
              </Field>
              <Field label="EWT %" hint="10% unless told otherwise">
                <input
                  className={inputClass}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="100"
                  step="0.01"
                  required
                  value={draft.ewtRate}
                  onChange={(event) => setDraft({ ...draft, ewtRate: event.target.value })}
                />
              </Field>
            </div>
            {Number(draft.grossAmount) > 0 && (
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Withheld {peso((Number(draft.grossAmount) * Number(draft.ewtRate || 0)) / 100)} · net payable{' '}
                <strong>{peso(Number(draft.grossAmount) * (1 - Number(draft.ewtRate || 0) / 100))}</strong>
              </p>
            )}
            <Field label="Notes">
              <textarea
                className={inputClass}
                rows={2}
                maxLength={1000}
                value={draft.notes}
                onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              />
            </Field>
            {error && <Alert tone="critical">{error}</Alert>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button type="submit" busy={save.isPending}>
                Save
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {deleting && (
        <Modal
          title="Delete commission?"
          description={`${deleting.categoryLabel} to ${deleting.payee}, ${peso(deleting.grossAmount)} gross.`}
          onClose={() => setDeleting(null)}
        >
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Keep it
            </Button>
            <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate(deleting)}>
              Delete
            </Button>
          </div>
        </Modal>
      )}
    </Card>
  );
}

/** On the project: read-only, with the payable each commission became. */
export function ProjectCommissionsCard({ rows }: { rows: CommissionRow[] | undefined }): JSX.Element | null {
  if (!rows || rows.length === 0) return null;
  const add = (key: 'grossAmount' | 'ewtAmount' | 'netPayable'): string =>
    String(Math.round(rows.reduce((total, row) => total + Number(row[key]) * 100, 0)) / 100);
  return (
    <Card
      title="Commissions"
      description="From the deal. Each becomes a payable in Accounts Payable when the project is completed (net of the 10% EWT)."
    >
      <CommissionTable rows={rows} totals={{ grossAmount: add('grossAmount'), ewtAmount: add('ewtAmount'), netPayable: add('netPayable') }} />
    </Card>
  );
}
